import {
  activityFeedContract,
  adyenContract,
  authContract,
  discountContract,
  drawContract,
  drawOrderContract,
  emailContract,
  feedbackContract,
  giftCardIntegrationContract,
  leaderboardContract,
  moderationContract,
  orderContract,
  paymentContract,
  playerContract,
  playerPreferencesContract,
  prizeContract,
  promoContract,
  referralContract,
  statsContract,
  subscriptionContract,
  tokenContract,
  winnerContract,
} from '@daymade/api-contracts';
import {
  ApiFetcher,
  ApiFetcherArgs,
  AppRouter,
  initClient,
  InitClientArgs,
  tsRestFetchApi,
  UnknownStatusError,
} from '@ts-rest/core';
// @ts-ignore
import { peekError } from '@daymade/common-lib/errors';
import getConfig from 'next/config';
import { handleNonOkResponse, LooseApiErrorResponse } from './api/response';
import { getCookie } from './cookie';
import HttpStatusCode from './HttpStatusCode';

const { publicRuntimeConfig } = getConfig();
export const API_ENDPOINT = publicRuntimeConfig.API_ENDPOINT;
console.log('API_ENDPOINT', API_ENDPOINT);

/** An error object that can be thrown when the TSRest client receives a 401 (unauthorised) response */
export class ApiUnauthorizedClientError extends Error {
  constructor(
    message: string,
    public readonly response: Awaited<ReturnType<ApiFetcher>>,
  ) {
    super(message);
  }
}

let addedGlobalErrorHandlerForUnknownStatus = false;
/** Attaches event handlers to window for error and unhandledrejection. This is the only way for us to globally handle
 * TSRest's UnknownStatusError as it happens after the the custom fetch API that we can provide to TSRest's initClient
 */
function ensureGlobalErrorHandlerForUnknownStatus() {
  if (typeof window === 'undefined') {
    return;
  }

  if (!addedGlobalErrorHandlerForUnknownStatus) {
    addedGlobalErrorHandlerForUnknownStatus = true;
    const unknownStatusErrorHandler = (
      event: PromiseRejectionEvent | ErrorEvent,
    ) => {
      const error = 'error' in event ? event.error : event.reason;
      if (error instanceof UnknownStatusError) {
        handleNonOkResponse(
          {
            status: 'error',
            error: peekError(error),
          },
          undefined,
        );
      }
    };
    window.addEventListener('error', unknownStatusErrorHandler);
    window.addEventListener('unhandledrejection', unknownStatusErrorHandler);
  }
}

/** Initialises a TSRest client with the sensible options for our usage of the API. Most importantly, it overrides the
 * fetch behaviour by wrapping it in some error handling.
 */
function initDaymadeClient<
  T extends AppRouter,
  TClientArgs extends InitClientArgs,
>(
  contract: T,
  // We must omit baseUrl, since it is provided by initDaymadeClient.
  // We must also omit baseHeaders, as that affects the type inference of the client that is returned
  options: Omit<TClientArgs, 'baseUrl' | 'baseHeaders'>,
) {
  // Ensure that the global error handler for TSRest's UnknownStatusError is attached
  ensureGlobalErrorHandlerForUnknownStatus();

  const customisedOptions = {
    validateResponse: true,
    throwOnUnknownStatus: true,
    baseUrl: API_ENDPOINT,
    baseHeaders: {
      'x-app-source': 'ts-rest',
      'x-access-token': () => getCookie('jwt', null),
    },
    api: async (args: ApiFetcherArgs) => {
      let response: Awaited<ReturnType<typeof tsRestFetchApi>>;
      try {
        response = await tsRestFetchApi(args);
      } catch (e) {
        // If an exception occurs here, it implies that either there is no network connection.
        //
        // Note that TSRest unknown status exceptions are not handled here. There is no way for us to catch those, since
        // they happen after the overridden 'api'. See unknownStatusErrorHandler.
        //
        // Show an error toast, log the error, and throw the exception so the calling-code has to catch the
        // exception or stop running
        handleNonOkResponse(
          {
            status: 'error',
            error: peekError(e),
          },
          undefined,
        );
        console.error(e);
        throw e;
      }

      if (response.status === HttpStatusCode.UNAUTHORIZED) {
        if (
          response.body &&
          typeof response.body === 'object' &&
          'message' in response.body &&
          response.body.message == 'Unauthorized'
        ) {
          // If we receive a 401 (Unauthorized) and the body has a message that says 'Unauthorized', it implies that the
          // JWT token has expired or is missing. See jwt-auth.guard.
          //
          // We should show an error toast, and throw the exception so the calling-code has to catch the
          // exception or stop running
          handleNonOkResponse(response as LooseApiErrorResponse, undefined);
          throw new ApiUnauthorizedClientError(
            'Daymade TSRest client received ' +
              HttpStatusCode.UNAUTHORIZED +
              ' for path ' +
              args.path,
            response,
          );
        } else {
          // Otherwise, it must be some other 401, e.g. the social log process also uses 401 responses. We should not
          // show a toast nor throw an exception, so that code can handle the 401 accordingly
        }
      }

      return response;
    },
    ...options,
  };
  return initClient(contract, customisedOptions);
}

export const adyenApiClient = initDaymadeClient(adyenContract, {});

export const authApiClient = initDaymadeClient(authContract, {});

export const discountApiClient = initDaymadeClient(discountContract, {});

export const drawApiClient = initDaymadeClient(drawContract, {});

export const drawOrderApiClient = initDaymadeClient(drawOrderContract, {});

export const leaderboardApiClient = initDaymadeClient(leaderboardContract, {});

export const emailApiClient = initDaymadeClient(emailContract, {});

export const feedbackApiClient = initDaymadeClient(feedbackContract, {});

export const giftCardIntegrationApiClient = initDaymadeClient(
  giftCardIntegrationContract,
  {},
);

export const moderationApiClient = initDaymadeClient(moderationContract, {});

export const orderApiClient = initDaymadeClient(orderContract, {
  jsonQuery: true,
});

export const paymentApiClient = initDaymadeClient(paymentContract, {});

export const playerApiClient = initDaymadeClient(playerContract, {});

export const preferencesApiClient = initDaymadeClient(
  playerPreferencesContract,
  {},
);

export const prizeApiClient = initDaymadeClient(prizeContract, {});

export const promoApiClient = initDaymadeClient(promoContract, {});

export const referralApiClient = initDaymadeClient(referralContract, {});

export const statsApiClient = initDaymadeClient(statsContract, {});

export const subscriptionApiClient = initDaymadeClient(
  subscriptionContract,
  {},
);

export const tokenApiClient = initDaymadeClient(tokenContract, {});

export const winnerApiClient = initDaymadeClient(winnerContract, {});

export const activityFeedApiClient = initDaymadeClient(
  activityFeedContract,
  {},
);
